import { format } from "date-fns";
import { useMemo } from "react";
import { DateRange } from "react-day-picker";

import { processUsageData, createUsageLimits } from "../utils";
import { UsageType, UsageTypeKey } from "../types";

import { useOrganizationUsageChartData } from "@/queries/organizations";
import { useOrganizationSubscriptionData } from "@/providers";

const getRequestLogsLimit = ({
  hasLegacySubscription,
  legacyPlanName,
  defaultLimit,
}: {
  hasLegacySubscription: boolean;
  legacyPlanName?: string | null;
  defaultLimit: number | string;
}): number | string => {
  if (hasLegacySubscription) {
    if (!legacyPlanName) {
      return 5000;
    }
    return 100000;
  }

  return defaultLimit;
};

export const useUsageChartsData = ({ dateRange }: { dateRange: DateRange }) => {
  const {
    subscriptionStatus: subscriptionData,
    isLoadingSubscriptionStatus: isSubscriptionLoading,
    hasLegacySubscription,
    legacyPlanName,
  } = useOrganizationSubscriptionData();

  const currentPeriodStart = dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : undefined;
  const currentPeriodEnd = dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") : undefined;

  const { data, isFetching: isUsageDataLoading } = useOrganizationUsageChartData({
    start_date: currentPeriodStart!,
    end_date: currentPeriodEnd!,
    isSubscriptionLoading,
  });

  const usageLimits = useMemo(() => createUsageLimits(subscriptionData), [subscriptionData]);

  const usageTypes: UsageType[] = useMemo(
    () => [
      {
        key: "requestLogs" as UsageTypeKey,
        title: "Request Logs",
        description: "Monthly request logs usage across workspaces",
        limit: getRequestLogsLimit({
          hasLegacySubscription,
          legacyPlanName,
          defaultLimit: usageLimits.requestLogs ?? "Unlimited",
        }),
      },
      {
        key: "evalCells" as UsageTypeKey,
        title: "Evaluation Cells",
        description: "Monthly evaluation cell executions usage across workspaces",
        limit: usageLimits.evaluations,
      },
      {
        key: "agents" as UsageTypeKey,
        title: "Workflow Node Executions",
        description: "Monthly workflow node executions usage across workspaces",
        limit: usageLimits.agents,
      },
      {
        key: "traces" as UsageTypeKey,
        title: "Traces",
        description: "Monthly traces usage across workspaces",
        limit: usageLimits.traces,
      },
      {
        key: "playgroundRuns" as UsageTypeKey,
        title: "Playground Runs",
        description: "Daily playground runs usage across workspaces",
        limit: usageLimits.playgroundRuns,
      },
    ],
    [usageLimits, hasLegacySubscription, legacyPlanName],
  );

  const processedData = useMemo(
    () => processUsageData(data, currentPeriodStart!, currentPeriodEnd!),
    [data, currentPeriodStart, currentPeriodEnd],
  );

  return {
    isLoading: isUsageDataLoading || isSubscriptionLoading,
    usageTypes,
    processedData,
    totalOrganizationUsage: data?.total_organization_usage,
  };
};
